import React, { useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    TextField,
    Typography,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import AppDialogTitle from '../../../../ui/appDialogTitle';
import { ValidDataService } from '../../../../../service/validDataService';

const parseMultiValue = (value) => String(value || '')
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean);

const normalizeMultiValues = (values = []) => {
    const uniqueValues = new Map();
    values.forEach((value) => {
        const normalizedValue = String(value || '').trim();
        if (!normalizedValue) return;
        const key = normalizedValue.toLowerCase();
        if (!uniqueValues.has(key)) uniqueValues.set(key, normalizedValue);
    });
    return [...uniqueValues.values()];
};

const serializeMultiValue = (values) => normalizeMultiValues(values).join(', ');

const getFieldError = (field, value) => {
    if (!field.maxLength || String(value || '').length <= field.maxLength) return '';
    return `${field.label} cannot exceed ${field.maxLength} characters`;
};

const hasFieldErrors = (eyeState, fields) => fields.some((field) => (
    Boolean(getFieldError(field, eyeState.manualLens?.[field.key]))
));

const buildDefaultEyeState = () => ({
    price: '',
    quantity: 1,
    manualLens: {}
});

const hasEyeInput = (eyeState = {}, fields = []) => {
    if (!eyeState) return false;

    const hasPrice = eyeState.price !== '' && eyeState.price !== null && eyeState.price !== undefined;
    const hasQuantity = Number(eyeState.quantity || 1) !== 1;
    const hasManualFields = fields.some((field) => {
        const value = eyeState.manualLens?.[field.key];
        return value !== '' && value !== null && value !== undefined;
    });

    return hasPrice || hasQuantity || hasManualFields;
};

const normalizeFieldValue = (type, value) => {
    if (value === '' || value === null || value === undefined) return '';
    if (type === 'integer') {
        const next = Number.parseInt(value, 10);
        return Number.isNaN(next) ? '' : next;
    }
    if (type === 'number') {
        const next = Number(value);
        return Number.isNaN(next) ? '' : next;
    }
    return typeof value === 'string' ? value.trim() : value;
};

const cleanManualLensPayload = (manualLens = {}, fields = []) => {
    const payload = {};

    fields.forEach((field) => {
        const value = manualLens[field.key];
        if (value === undefined || value === null || value === '') return;
        payload[field.key] = normalizeFieldValue(field.type, value);
    });

    return payload;
};

const buildManualLensDescription = (eye, manualLens = {}) => {
    const descriptor = [
        ['Index', manualLens.indexValue],
        ['Coating', manualLens.coating],
        ['Brand', manualLens.brandName],
        ['Lens Type', manualLens.lensType],
    ]
        .filter(([, value]) => value !== undefined && value !== null && value !== '')
        .map(([label, value]) => `${label}: ${value}`)
        .join(' | ');
        // don't need to add manual lens to description because it is already implied by the entry method and the fact that it is a manual lens
    return descriptor ? `(${eye}): ${descriptor}` : `(${eye})`;
};

export default function ManualLensDialog({
    open,
    onClose,
    onSelect,
    existingManualLensData = null,
}) {
    const [schema, setSchema] = useState(null);
    const [od, setOd] = useState(buildDefaultEyeState());
    const [os, setOs] = useState(buildDefaultEyeState());

    const fields = useMemo(() => schema?.fields || [], [schema]);

    useEffect(() => {
        if (!open) return;

        const loadSchema = async () => {
            const response = await ValidDataService.getManualLensSchema();
            const schemaData = response?.data || response;
            setSchema(schemaData || null);
        };

        loadSchema();
    }, [open]);

    useEffect(() => {
        if (!open) return;

        if (!existingManualLensData) {
            setOd(buildDefaultEyeState());
            setOs(buildDefaultEyeState());
            return;
        }

        const odItem = existingManualLensData.odLens || null;
        const osItem = existingManualLensData.osLens || null;

        setOd({
            price: odItem?.price ?? '',
            quantity: odItem?.quantity ?? 1,
            manualLens: {
                ...(odItem?.itemConfig?.manualLens || {})
            }
        });

        setOs({
            price: osItem?.price ?? '',
            quantity: osItem?.quantity ?? 1,
            manualLens: {
                ...(osItem?.itemConfig?.manualLens || {})
            }
        });
    }, [open, existingManualLensData]);

    const handleEyeMetaChange = (eye, key, value) => {
        if (eye === 'OD') {
            setOd((prev) => ({ ...prev, [key]: value }));
            return;
        }
        setOs((prev) => ({ ...prev, [key]: value }));
    };

    const handleFieldChange = (eye, key, value) => {
        if (eye === 'OD') {
            setOd((prev) => ({ ...prev, manualLens: { ...prev.manualLens, [key]: value } }));
            return;
        }
        setOs((prev) => ({ ...prev, manualLens: { ...prev.manualLens, [key]: value } }));
    };

    const copyEye = (fromEye, toEye) => {
        const source = fromEye === 'OD' ? od : os;

        if (toEye === 'OD') {
            setOd({ ...source });
            return;
        }
        setOs({ ...source });
    };

    const buildItem = (eye, eyeState, groupId, existingItem = null) => {
        if (!hasEyeInput(eyeState, fields)) return null;

        const manualLensPayload = cleanManualLensPayload(eyeState.manualLens, fields);
        return {
            ...(existingItem || {}),
            id: existingItem?.id || uuidv4(),
            type: 'lens',
            entryMethod: 'manual',
            eye,
            itemId: null,
            groupId,
            quantity: Number(eyeState.quantity || 1),
            price: Number(eyeState.price || 0),
            tax: existingItem?.tax ?? 0,
            discount: existingItem?.discount ?? 0,
            total: 0,
            date: existingItem?.date || new Date().toISOString(),
            description: buildManualLensDescription(eye, manualLensPayload),
            itemConfig: {
                manualLens: manualLensPayload,
                manualEntry: true,
                model: manualLensPayload.lensType
                    ? { lensType: manualLensPayload.lensType }
                    : null
            }
        };
    };

    const handleSave = () => {
        if (!hasEyeInput(od, fields) && !hasEyeInput(os, fields)) {
            return;
        }
        if (hasFieldErrors(od, fields) || hasFieldErrors(os, fields)) {
            return;
        }

        const groupId = existingManualLensData?.groupId || uuidv4();

        const payload = {
            groupId,
            odLens: buildItem('OD', od, groupId, existingManualLensData?.odLens || null),
            osLens: buildItem('OS', os, groupId, existingManualLensData?.osLens || null),
        };

        onSelect(payload);
    };

    const renderEyeSection = (eye, eyeState) => (
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{eye}</Typography>
                <Box>
                    {eye === 'OD' && <Button size="small" onClick={() => copyEye('OD', 'OS')}>Copy to OS</Button>}
                    {eye === 'OS' && <Button size="small" onClick={() => copyEye('OS', 'OD')}>Copy to OD</Button>}
                </Box>
            </Box>

            <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                    <TextField
                        size="small"
                        fullWidth
                        label="Price"
                        value={eyeState.price}
                        onChange={(e) => handleEyeMetaChange(eye, 'price', e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        size="small"
                        fullWidth
                        label="Quantity"
                        type="number"
                        value={eyeState.quantity}
                        onChange={(e) => handleEyeMetaChange(eye, 'quantity', e.target.value)}
                    />
                </Grid>
                {fields.map((field) => {
                    const currentValue = eyeState.manualLens[field.key] ?? '';
                    const fieldError = getFieldError(field, currentValue);
                    const inputProps = field.maxLength ? { maxLength: field.maxLength } : undefined;

                    return (
                        <Grid item xs={12} md={field.multiline ? 12 : 6} key={`${eye}-${field.key}`}>
                            {field.control === 'autocomplete' ? (
                                <Autocomplete
                                    autoSelect
                                    disableCloseOnSelect={Boolean(field.multiple)}
                                    filterSelectedOptions={Boolean(field.multiple)}
                                    freeSolo={field.allowCustom !== false}
                                    limitTags={field.limitTags || 2}
                                    multiple={Boolean(field.multiple)}
                                    options={field.options || []}
                                    sx={field.multiline ? {
                                        '& .MuiAutocomplete-inputRoot': {
                                            minHeight: 96,
                                            alignContent: 'flex-start',
                                            alignItems: 'flex-start',
                                        },
                                    } : undefined}
                                    value={field.multiple ? parseMultiValue(currentValue) : currentValue}
                                    onChange={(_, value) => {
                                        handleFieldChange(
                                            eye,
                                            field.key,
                                            field.multiple ? serializeMultiValue(value) : (value || '')
                                        );
                                    }}
                                    onInputChange={field.multiple ? undefined : (_, value) => {
                                        handleFieldChange(eye, field.key, value);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            label={field.label}
                                            placeholder={field.allowCustom !== false ? 'Select or type' : 'Select'}
                                            error={Boolean(fieldError)}
                                            helperText={fieldError}
                                            inputProps={{ ...params.inputProps, ...inputProps }}
                                        />
                                    )}
                                />
                            ) : (
                                <TextField
                                    size="small"
                                    fullWidth
                                    multiline={Boolean(field.multiline)}
                                    minRows={field.multiline ? 2 : 1}
                                    label={field.label}
                                    value={currentValue}
                                    error={Boolean(fieldError)}
                                    helperText={fieldError}
                                    inputProps={inputProps}
                                    onChange={(e) => handleFieldChange(eye, field.key, e.target.value)}
                                />
                            )}
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <AppDialogTitle onClose={onClose} title={existingManualLensData ? 'Edit Manual Lens' : 'Add Manual Lens'} />
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        {renderEyeSection('OD', od)}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {renderEyeSection('OS', os)}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={hasFieldErrors(od, fields) || hasFieldErrors(os, fields)}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
